import React, { useEffect, useState } from 'react';
import { useApp } from '../context/AppContext';

// ── The Supervisor board ───────────────────────────────────────────────────
//
// Every open work order with something wrong, worst first, and one sentence
// each saying what. It answers the questions Julian currently answers by
// scrolling: why hasn't #128 moved, why has this estimate been waiting since
// Tuesday, why is tomorrow's visit still unconfirmed.
//
// Two things it deliberately is not:
//
//   It is not a list of every job. A board that shows the healthy ones is a
//   board nobody scrolls to the bottom of, so a job with nothing wrong simply
//   is not here — and an empty board is the good outcome, said in words.
//
//   It is not the agents' output. Everything here is already being chased.
//   This is what the chasing has not fixed, which is a much shorter list and
//   the only one worth a person's morning.
export default function FollowThrough() {
  const {
    agentBoard, isLoadingBoard, boardBrief, loadAgentBoard, pauseJob, setActiveRequest, goTo,
    autoSetup, isSavingAuto, loadAutoDispatch, saveAutoDispatch,
  } = useApp();
  const [pausing, setPausing] = useState(null);
  const [reason, setReason] = useState('');

  // Load once when the tab is opened. loadAgentBoard is recreated on every
  // render, so listing it here would poll forever.
  useEffect(() => {
    loadAgentBoard();
    loadAutoDispatch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = agentBoard?.rows || [];
  const tally = agentBoard?.tally || {};

  const tone = (r) => {
    if (r.priority === 'emergency') return 'emergency';
    if (r.needsHuman) return 'you';
    if (r.priority === 'urgent') return 'urgent';
    return 'routine';
  };

  const hold = async (jobId) => {
    if (!reason.trim()) return;
    await pauseJob(jobId, true, reason.trim());
    setPausing(null);
    setReason('');
  };

  return (
    <div style={{ padding: '0 16px 24px' }}>
      <div className="ft-head">
        <h1 className="section-title" style={{ margin: 0 }}>What needs chasing</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-outline btn-sm" disabled={isLoadingBoard}
            onClick={() => loadAgentBoard()}>
            {isLoadingBoard ? 'Looking…' : 'Refresh'}
          </button>
          <button className="btn btn-outline btn-sm" disabled={isLoadingBoard}
            onClick={() => loadAgentBoard({ brief: true })}>
            Read it to me
          </button>
        </div>
      </div>

      {agentBoard?.error && <div className="alert alert-red">{agentBoard.error}</div>}

      {/* ── Auto-dispatch ──────────────────────────────────────────────
          Off until he turns it on, and the panel says in plain words what
          happens when he does. A switch whose effect you have to remember is
          a switch nobody touches. */}
      {autoSetup && (() => {
        const s = autoSetup.settings || {};
        const used = autoSetup.usedThisHour || 0;
        return (
          <div className={`ad-box${s.enabled ? ' on' : ''}`}>
            <div className="ad-top">
              <div>
                <div className="ad-title">Offer jobs without me</div>
                <div className="ad-sub">
                  {s.enabled
                    ? 'A new work order goes straight to the best technician, day or night.'
                    : 'Off. Every job waits for you to offer it.'}
                </div>
              </div>
              <button type="button" role="switch" aria-checked={s.enabled}
                aria-label="Offer jobs without me"
                className={`ad-switch${s.enabled ? ' on' : ''}`}
                disabled={isSavingAuto}
                onClick={() => saveAutoDispatch({ enabled: !s.enabled })}>
                <span className="ad-knob" />
              </button>
            </div>

            {s.enabled && (
              <>
                {/* What it will not do, said once, on the screen rather than
                    in a document nobody opens. */}
                <ul className="ad-rules">
                  <li>Anything with gas, smoke or damage to the building comes to you instead.</li>
                  <li>Nothing is sent until the unit number and the way in are on the job.</li>
                  <li>Routine jobs overnight go to the app only. No texts until morning.</li>
                  <li>An emergency goes out straight away, whatever the hour.</li>
                  <li>Stops after {s.maxPerHour} offers in an hour and waits for you.</li>
                </ul>
                <div className="ad-meter">
                  <span>{used} of {s.maxPerHour} automatic offers this hour</span>
                  <div className="ad-bar"><i style={{ width: `${Math.min(100, (used / (s.maxPerHour || 1)) * 100)}%` }} /></div>
                </div>
              </>
            )}
          </div>
        );
      })()}

      <div className="ft-tally">
        <div className="ft-stat"><b>{tally.jobs || 0}</b><span>Need chasing</span></div>
        <div className={`ft-stat${tally.needingHuman ? ' hot' : ''}`}><b>{tally.needingHuman || 0}</b><span>Need you</span></div>
        <div className={`ft-stat${tally.emergencies ? ' hot' : ''}`}><b>{tally.emergencies || 0}</b><span>Emergency</span></div>
      </div>

      {/* The written read, only when it has been asked for — it costs a model
          call, and the board below it is the substance either way. */}
      {boardBrief && (
        <div className="ft-brief">
          <div className="ft-brief-head">{boardBrief.headline}</div>
          {(boardBrief.doFirst || []).map((d) => {
            const row = rows.find((r) => r.jobId === d.jobId);
            return (
              <div key={d.jobId} className="ft-brief-item">
                <strong>{row?.where || 'A job'}</strong> — {d.because}
              </div>
            );
          })}
          {boardBrief.pattern && <div className="ft-brief-pattern">{boardBrief.pattern}</div>}
        </div>
      )}

      {!rows.length && !isLoadingBoard && (
        <div className="ft-quiet">
          Nothing is stuck.<br />
          Every open job is waiting on something that has not come due yet.
        </div>
      )}

      {rows.map((r) => (
        <div key={r.jobId} className="ft-row" data-tone={tone(r)}>
          <div className="ft-row-top">
            <span className="ft-where">{r.where || 'Work order'}</span>
            <span className="ft-stalled">{r.stalledFor} quiet</span>
          </div>

          <div className="ft-why">{r.why}</div>

          {/* The rest of what is wrong, where there is more than one thing.
              Named rather than counted: "and 2 more" is not information. */}
          {r.concerns.length > 1 && (
            <div className="ft-more">
              Also: {r.concerns.slice(1, 3).map((c) => c.title.toLowerCase()).join(', ')}
            </div>
          )}

          <div className="ft-chips">
            {r.needsHuman && <span className="ft-chip you">Waiting on you</span>}
            {r.priority !== 'routine' && <span className="ft-chip">{r.priority}</span>}
            <span className="ft-chip">{r.status}</span>
            {r.number && <span className="ft-chip">{r.number}</span>}
          </div>

          {pausing === r.jobId ? (
            <>
              <label className="offer-label" htmlFor={`hold-${r.jobId}`}>
                Why is it on hold? This shows on the board instead of the problem.
              </label>
              <input id={`hold-${r.jobId}`} type="text" className="offer-input"
                value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder="Waiting on a part until Tuesday" />
              <div className="ft-actions">
                <button className="btn btn-ghost btn-sm" onClick={() => { setPausing(null); setReason(''); }}>Back</button>
                <button className="btn btn-primary btn-sm" disabled={!reason.trim()} onClick={() => hold(r.jobId)}>
                  Put it on hold
                </button>
              </div>
            </>
          ) : (
            <div className="ft-actions">
              <button className="btn btn-outline btn-sm"
                onClick={() => { setActiveRequest({ _id: r.jobId }); goTo && goTo('admin'); }}>
                Open it
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => { setPausing(r.jobId); setReason(''); }}>
                On hold
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
